import { useId, useState } from 'react';

import { MtxButton, MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import Collapse from '@/components/Collapse/Collapse';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import toTrackingSlug from '@/utils/functions/tracking/toTrackingSlug/toTrackingSlug';

import styles from './styles.module.css';

type Performance = {
  ytd?: number | null;
  oneYear?: number | null;
  threeYears?: number | null;
  fiveYears?: number | null;
};

type CardSupportProps = {
  badge: string;
  badgeColor: string;
  name: string;
  isin: string;
  manager: string;
  montantDesinvesti: number | null;
  pourcentage: number | null;
  noteRisque?: number | null;
  performances?: Performance;
  ficheUrl?: string;
  messageBlocage?: string | null;
  lienAvenantUrl?: string | null;
  isOpen?: boolean;
  onToggle?: () => void;
  isInvested?: boolean;
};

const isNumber = (value: unknown): value is number => typeof value === 'number';

const CardSupport = ({
  badge,
  badgeColor,
  name,
  isin,
  manager,
  montantDesinvesti,
  pourcentage,
  noteRisque,
  performances,
  ficheUrl,
  messageBlocage,
  lienAvenantUrl,
  isOpen: isOpenProp,
  onToggle,
  isInvested = false,
}: CardSupportProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const panelId = `card-support-panel-${uid}`;
  const headerId = `card-support-header-${uid}`;
  const [isOpenInternal, setIsOpenInternal] = useState(false);

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : isOpenInternal;

  const toggleOpen = () => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'placement',
      event_nom: 'plus_d_informations',
      event_detail_1: 'details_des_supports',
      event_detail_2: toTrackingSlug(name),
    });
    if (isControlled) {
      onToggle?.();
    } else {
      setIsOpenInternal((prev) => !prev);
    }
  };

  const formatPerf = (value: number) => `${value > 0 ? '+' : ''}${t('format.percentFlexible', { value })}`;

  const hasAnyPerformance =
    performances !== undefined &&
    (isNumber(performances.ytd) ||
      isNumber(performances.oneYear) ||
      isNumber(performances.threeYears) ||
      isNumber(performances.fiveYears));

  return (
    <div className={`${styles.card} ${isOpen ? styles.cardOpen : ''}`}>
      <div className={styles.cardBody}>
        <div className={styles.cardHeader} data-equalize="header">
          <div className={styles.badgeWrapper}>
            {badge && (
              <span className={styles.badge} style={{ color: badgeColor, borderColor: badgeColor }}>
                {badge}
              </span>
            )}
          </div>

          <div className={styles.titleBlock}>
            <p className={styles.name}>{name}</p>
            <p className={styles.subtitle}>{isin}</p>
            <p className={styles.subtitle}>{manager}</p>
          </div>
        </div>

        <div className={`${styles.repartition} ${isOpen ? styles.repartitionOpen : ''}`}>
          <div className={styles.repartitionItem}>
            <span className={styles.repartitionLabel}>
              {isInvested ? t('cardSupport.montantInvesti') : t('cardSupport.montantDesinvesti')}
            </span>
            <span className={styles.repartitionValue}>
              {isNumber(montantDesinvesti)
                ? t('format.currencyFlexible', { value: montantDesinvesti })
                : t('common.infoUnavailable')}
            </span>
          </div>
          <div className={styles.repartitionItem}>
            <span className={styles.repartitionLabel}>{t('cardSupport.pourcentage')}</span>
            <span className={styles.repartitionValue}>
              {isNumber(pourcentage)
                ? t('format.percentFlexible', { value: pourcentage })
                : t('common.infoUnavailable')}
            </span>
          </div>
        </div>

        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          aria-hidden={!isOpen}
          className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        >
          <Collapse isOpen={isOpen}>
            <div className={styles.panelContent} data-equalize="panel">
              {isNumber(noteRisque) && (
                <div className={styles.riskBox}>
                  <p className={styles.riskLabel}>{t('cardSupport.noteRisque').toUpperCase()}</p>
                  <div className={styles.riskValueWrapper}>
                    <span className={styles.riskScore}>{noteRisque}</span>
                    <span className={styles.riskMax}>{t('cardSupport.noteRisqueMax')}</span>
                  </div>
                </div>
              )}

              {performances !== undefined && (
                <>
                  <div className={styles.performancesSection}>
                    <p className={styles.performancesTitle}>{t('cardSupport.performances')}</p>
                    <div className={styles.performancesGrid}>
                      {hasAnyPerformance ? (
                        <>
                          {isNumber(performances.ytd) && (
                            <div className={styles.performanceCell}>
                              <span className={styles.performancePeriod}>{t('cardSupport.ytd')}</span>
                              <span className={styles.performanceValue}>{formatPerf(performances.ytd)}</span>
                            </div>
                          )}
                          {isNumber(performances.oneYear) && (
                            <div className={styles.performanceCell}>
                              <span className={styles.performancePeriod}>{t('cardSupport.oneYear')}</span>
                              <span className={styles.performanceValue}>{formatPerf(performances.oneYear)}</span>
                            </div>
                          )}
                          {isNumber(performances.threeYears) && (
                            <div className={styles.performanceCell}>
                              <span className={styles.performancePeriod}>{t('cardSupport.threeYears')}</span>
                              <span className={styles.performanceValue}>{formatPerf(performances.threeYears)}</span>
                            </div>
                          )}
                          {isNumber(performances.fiveYears) && (
                            <div className={styles.performanceCell}>
                              <span className={styles.performancePeriod}>{t('cardSupport.fiveYears')}</span>
                              <span className={styles.performanceValue}>{formatPerf(performances.fiveYears)}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={styles.performanceCell}>
                          <span className={styles.performanceValue}>{t('cardSupport.nc')}</span>
                        </div>
                      )}
                    </div>

                    {ficheUrl && (
                      <a href={ficheUrl} className={styles.ficheLink} target="_blank" rel="noopener noreferrer">
                        <i className="totem-icon totem-icon-external-link" aria-hidden="true" />
                        <span>{t('cardSupport.ficheInfo')}</span>
                      </a>
                    )}

                    {messageBlocage != null && messageBlocage !== '' && (
                      <div className={styles.messageBox}>
                        <MtxIcon name="info-circle" size={20} className={styles.messageIcon} />
                        <p className={styles.messageText}>
                          {messageBlocage}
                          {lienAvenantUrl != null && lienAvenantUrl !== '' && (
                            <>
                              {' '}
                              <a
                                href={lienAvenantUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.messageLink}
                              >
                                {t('cardSupport.lienAvenant')}
                              </a>{' '}
                              {t('cardSupport.lienAvenantSuffix')}
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Collapse>
        </div>
      </div>

      <MtxButton
        id={headerId}
        className={styles.toggleButton}
        variant="tertiary"
        size="medium"
        fullWidth
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span>{isOpen ? t('cardSupport.lessInfo') : t('cardSupport.moreInfo')}</span>
        <MtxIcon className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} name="chevron-down" size={20} />
      </MtxButton>
    </div>
  );
};

export type { CardSupportProps, Performance };
export default CardSupport;
